import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useImpersonationStore } from '@/stores/impersonationStore';
import { canAccess, hasAnyRole, type Role } from '@/lib/permissions';

interface Props {
  /** Explicit roles required. If omitted, the path's entry in ACCESS is used. */
  roles?: Role[];
  children: ReactNode;
}

/**
 * Wraps a route. Unauthenticated → /login. Authenticated but not permitted
 * → /403. Permitted → renders children.
 */
export function RequireRole({ roles, children }: Props) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  const impersonating = useImpersonationStore((s) => s.tenant);

  if (!token) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // A platform admin has no tenant_id of their own, so every /admin request
  // would 400 with "no tenant in scope" until they pick one from
  // /super/tenants. Redirect there instead of rendering a page that can
  // only fail.
  const isPlatformAdmin = user?.roles?.some((r) => ['super_admin', 'platform_admin'].includes(r));
  if (isPlatformAdmin && !impersonating && location.pathname.startsWith('/admin')) {
    return <Navigate to="/super/tenants" replace />;
  }

  const ok = roles
    ? hasAnyRole(user?.roles, roles)
    : canAccess(location.pathname, user?.roles, user?.permissions, user?.role_overrides);

  if (!ok) {
    return <Navigate to="/403" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}
